import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Row, Col, message, Spin, GlobalToken } from 'antd';
import { PlusOutlined, LoadingOutlined } from '@ant-design/icons';
import { reject, replace, find, get, flatMap, uniqBy, filter, isNil, sortBy } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { observer } from 'mobx-react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { EPInput, EPTextArea, NavBack, SeverityIcon } from '@/comp';
import { useStores } from '@/hooks/use-stores';
import { useMeasurement } from '@/hooks/useMeasurement';
import { metricsDetails, permissions } from '@/constants';
import { MetricType } from '@/constants/metric-type.enum';
import { toTranslationFormat } from '@/utils/util-functions';
import IconDelete from '@/assets/icon-delete.svg?react';
import { EPButton, EPSelect, EPSelectOption } from '@/components';
import { AlertLevels } from '@/constants/enums';

const createDevice = (withOperator?: boolean) => {
  return {
    index: uuidv4(),
    id: undefined,
    name: undefined,
    operator: withOperator ? '+' : undefined,
  };
};

const initialFormState = {
  name: '',
  longDescription: '',
  severity: undefined,
  duration: '0',
  devices: [],
  selectedDevices: [],
  operator: '',
  threshold: '',
  metric: undefined as MetricType,
};

export interface Rule {
  id: string;
  alertType: string;
  title: string;
  detailedDescription: string;
  severity: AlertLevels;
  conditions: {
    conditions: {
      metric: string;
      formula: () => string;
      operator: string;
      threshold: number;
    };
  };
  historyAttributes: {
    duration: string;
    units: string;
  };
  deviceName?: string;
  createdDate?: string;
  originalTemplate: {
    severity: AlertLevels;
    title: string;
    detailedDescription: string;
  };
}

interface Payload {
  name: string;
  longDescription: string;
  severity: string;
  conditions: {
    conditions: {
      metric: string;
      formula: () => string;
      operator: string;
      threshold: number;
    }[];
    match: 'match_all';
  };
  historyAttributes: {
    duration: number;
    units: string;
  };
  state?: string;
}

const CreateOrEditCustomRule = observer(({ rule }: { rule?: Rule }) => {
  const { stationId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { tunit, tvalue, measurementSystem } = useMeasurement();
  const { devicesStore, rulesStore, permissionStore } = useStores();
  const [formState, setFormState] = useState(initialFormState);
  const [submittingState, setSubmittingState] = useState('none'); // none, create, create-and-activate
  const mode = rule ? 'update' : 'create';
  const submitButtonTxt = mode === 'update' ? t('Update') : t('Create');
  const titleTxt = mode === 'update' ? t('Update a custom rule') : t('Create a custom rule');
  const isFormDisabled = !permissionStore.isAuth(permissions.UPDATE_CUSTOM_ALERT) || submittingState !== 'none';
  const disallowedMetrics = [
    MetricType.ControlMode,
    MetricType.NextServiceName,
    MetricType.PipeDiameter,
    MetricType.MaxInverterSpeedRPM,
    MetricType.EstimatedSpecificPower,
  ];
  const allowedMetrics = filter(devicesStore.metricsKeys, metricKey => !disallowedMetrics.includes(metricKey));
  const backUrl = location.state?.from ? location.state?.from : `/home/station/${stationId}/rules/custom`;

  const onChangeName = e =>
    setFormState({
      ...formState,
      name: e.target.value,
    });

  const onChangeDescription = e =>
    setFormState({
      ...formState,
      longDescription: e.target.value,
    });

  const onChangeSeverity = value =>
    setFormState({
      ...formState,
      severity: value,
    });

  const onChangeDuration = value =>
    setFormState({
      ...formState,
      duration: value,
    });

  const onChangeMetric = value => {
    const shouldReset = value === 'activity' || formState.metric === 'activity';

    setFormState({
      ...formState,
      metric: value,
      selectedDevices: [createDevice()],
      threshold: shouldReset ? '' : formState.threshold,
      operator: shouldReset ? '' : formState.operator,
    });
  };

  const onChangeOperator = value =>
    setFormState({
      ...formState,
      operator: value,
    });

  const onChangeThreshold = e => {
    const value = e.target.value;

    if (
      !(value.length <= 1 && (value === '-' || value === '+' || value === '')) &&
      !(!isNaN(parseFloat(value)) && isFinite(value))
    ) {
      return;
    }

    setFormState({
      ...formState,
      threshold: value.trim(),
    });
  };

  const onChangeActivityThreshold = value => {
    setFormState({
      ...formState,
      threshold: value,
    });
  };

  const onChangeDevice = (index, id) => {
    const newDevice = find(formState.devices, { id });
    const newSelectedDevices = [...formState.selectedDevices];
    newSelectedDevices[index].id = id;
    newSelectedDevices[index].name = newDevice.name;
    setFormState({
      ...formState,
      selectedDevices: newSelectedDevices,
    });
  };

  const onChangeSelectedDeviceOperator = (index, operator) => {
    const newSelectedDevices = [...formState.selectedDevices];
    newSelectedDevices[index].operator = operator;
    setFormState({
      ...formState,
      selectedDevices: newSelectedDevices,
    });
  };

  const onClickAddDevice = () =>
    setFormState({
      ...formState,
      selectedDevices: [...formState.selectedDevices, createDevice(true)],
    });

  const onClickRemoveDevice = index => {
    const newSelectedDevices = reject(formState.selectedDevices, { index });
    setFormState({
      ...formState,
      selectedDevices: newSelectedDevices,
    });
  };

  const onSubmit = async shouldActivate => {
    const getFormula = () => {
      return formState.selectedDevices.reduce((acc, device) => {
        return acc + (device.operator ? ` ${device.operator} ` : '') + `{${device.id}}`;
      }, '');
    };
    const getHistoryAttributes = () => {
      return formState.duration === '0'
        ? null
        : {
            duration: Number(formState.duration),
            units: 'minute',
          };
    };
    const payload: Payload = {
      name: formState.name.trim(),
      longDescription: formState.longDescription.trim(),
      severity: formState.severity,
      conditions: {
        conditions: [
          {
            metric: formState.metric,
            formula: getFormula(),
            operator: formState.operator,
            threshold:
              formState.metric === 'activity'
                ? formState.threshold
                : tvalue(
                    Number(formState.threshold),
                    tunit(metricsDetails[formState.metric]?.unit),
                    'METRIC',
                    measurementSystem,
                  ),
          },
        ],
        match: 'match_all',
      },
      historyAttributes: getHistoryAttributes(),
    };
    const alertType = rule?.alertType.toLowerCase() || 'custom';

    try {
      shouldActivate ? setSubmittingState('create-and-activate') : setSubmittingState('create');
      switch (mode) {
        case 'update':
          await rulesStore.update(payload, alertType, rule.id);
          message.success(t('Alert rule updated successfully'));
          break;

        case 'create':
          payload.state = shouldActivate ? 'activated' : 'deactivated';
          await rulesStore.create(payload, alertType);
          message.success(t('Alert rule created successfully'));
          break;
        default:
          break;
      }
      setTimeout(() => navigate(backUrl), 0);
    } catch (e) {
      message.error('Something went wrong');
    } finally {
      setSubmittingState('none');
    }
  };

  const isFormValid = () => {
    const isSelectedDevicesValid = () => {
      return formState.selectedDevices.reduce((acc, device, index) => {
        return index > 1 ? (device.id && device.operator ? acc : false) : device.id ? acc : false;
      }, true);
    };
    return (
      formState.name &&
      formState.longDescription &&
      formState.severity &&
      formState.metric &&
      isSelectedDevicesValid() &&
      formState.operator &&
      formState.threshold
    );
  };

  const getAvailableDevices = currentSelectedDeviceId => {
    const selectedDevicesIds = formState.selectedDevices.map(d => d.id);
    const availableDevices = formState.devices.filter(
      d => !selectedDevicesIds.includes(d.id) || d.id === currentSelectedDeviceId,
    );
    const availableDevicesSorted = sortBy(availableDevices, 'name');
    return availableDevicesSorted;
  };

  const getMetricText = metric => {
    const text = get(metricsDetails, `${metric}.text`, metric);
    return t(toTranslationFormat(text));
  };

  const getMetricUnit = metric => {
    const unit = get(metricsDetails, `${metric}.unit`, '');
    const convertedUnit = tunit(unit);
    const translatedUnit = t(convertedUnit);
    return translatedUnit;
  };

  useEffect(() => {
    const newDevices = devicesStore.metrics[formState.metric] || [];
    // For 'onDevices' we want to present the parent's device name and not the actual device
    const allDevices = flatMap(devicesStore.metrics);
    const uniqDevices = uniqBy(allDevices, 'id');
    const devicesWithParentDevice = filter(uniqDevices, device => !isNil(device.parentDeviceId));

    newDevices.forEach(device => {
      const deviceWithParent = find(devicesWithParentDevice, { id: device.id });
      if (!isNil(deviceWithParent)) {
        const parentDevice = find(uniqDevices, { id: deviceWithParent.parentDeviceId });
        if (!isNil(parentDevice)) {
          device.name = parentDevice.name;
        }
      }
    });

    setFormState(oldState => ({
      ...oldState,
      devices: [...newDevices],
    }));
  }, [formState.metric]);

  useEffect(() => {
    if (!rule) return;

    const {
      title: name,
      detailedDescription: longDescription,
      severity,
      conditions: { conditions },
    } = rule;
    let { historyAttributes = { duration: null, units: null } } = rule;
    historyAttributes = historyAttributes || { duration: null, units: null };
    const { duration } = historyAttributes;
    const { metric, formula, devicesNames, operator, threshold } = conditions[0];
    const newSelectedDevices = (() => {
      const newDevices = [];
      const formulaArr = formula.split(' ');

      for (let i = 0; i < formulaArr.length; i++) {
        const device = createDevice();

        // handle formula's first device
        if (i === 0) {
          device.id = replace(formulaArr[i], /[{}]/g, '');
          device.name = devicesNames[i];
        }
        // handle formula's rest of devices
        else {
          device.id = replace(formulaArr[i + 1], /[{}]/g, '');
          device.name = devicesNames[(i - 1) / 2 + 1];
          device.operator = formulaArr[i];
          i++;
        }
        newDevices.push(device);
      }
      return newDevices;
    })();

    setFormState({
      ...formState,
      name,
      longDescription,
      severity: severity?.toLowerCase(),
      duration: duration ? duration : '0',
      operator,
      threshold: metric === 'activity' ? threshold : tvalue(threshold, metricsDetails[metric]?.unit),
      metric,
      selectedDevices: [...formState.selectedDevices, ...newSelectedDevices],
    });
  }, [rule]);

  const selectedDevicesElements = formState.selectedDevices.map((device, index) => (
    <div key={device.index} className="device-container">
      {device.operator && (
        <Row align="bottom" className="row-item">
          <Col span={12}>
            <div className="form-group form-group-device-operator">
              <label className="mandatory" htmlFor="">
                {t('Operation')}
              </label>
              <EPSelect
                value={device.operator}
                getPopupContainer={() => document.querySelector('.form-group.form-group-device-operator')}
                disabled={isFormDisabled}
                onChange={value => onChangeSelectedDeviceOperator(index, value)}
                className="ep-select"
              >
                <EPSelectOption value="+">+</EPSelectOption>
                <EPSelectOption value="-">-</EPSelectOption>
                <EPSelectOption value="*">*</EPSelectOption>
                <EPSelectOption value="/">/</EPSelectOption>
              </EPSelect>
            </div>
          </Col>
          <Col span={12}>
            <p className="form-group-device-operator-description">{t(`Follow the math order of operations.`)}</p>
          </Col>
        </Row>
      )}

      <Row className="row-item">
        <Col span={24}>
          <div className="form-group form-group-device relative">
            {index !== 0 && <IconDelete onClick={() => onClickRemoveDevice(device.index)} className="icon-delete" />}
            <label className="mandatory" htmlFor="">
              {t('Device')}
            </label>
            <EPSelect
              value={device.id}
              getPopupContainer={() => document.querySelector('.form-group.form-group-device')}
              disabled={!formState.metric || isFormDisabled}
              onChange={id => onChangeDevice(index, id)}
              className="ep-select"
            >
              {getAvailableDevices(device.id).map(d => (
                <EPSelectOption key={d.id} value={d.id}>
                  {d.name}
                </EPSelectOption>
              ))}
            </EPSelect>
          </div>
        </Col>
      </Row>
    </div>
  ));

  return (
    <Container>
      <NavBack url={backUrl} />

      <div className="form-container">
        <h1 className="title">{titleTxt}</h1>
        <h3 className="subtitle">{t('Define the logic for triggering alerts of different severity levels.')}</h3>

        <section>
          <NumberedTitle number={1} title={t('Name the alerts and set their severity.')} />

          <Row className="row-item">
            <Col span={24}>
              <div className="form-group">
                <label className="mandatory" htmlFor="">
                  {t('Rule name (Title)')}
                </label>
                <EPInput
                  placeholder={t('E.g. High than usual flow causes pressure drop')}
                  value={formState.name}
                  disabled={isFormDisabled}
                  onChange={onChangeName}
                />
              </div>
            </Col>
          </Row>

          <Row className="row-item">
            <Col span={24}>
              <div className="form-group">
                <label className="mandatory" htmlFor="">
                  {t('Description')}
                </label>
                <EPTextArea
                  placeholder={t('E.g. Try do the following...')}
                  rows={3}
                  value={formState.longDescription}
                  disabled={isFormDisabled}
                  onChange={onChangeDescription}
                />
              </div>
            </Col>
          </Row>

          <Row align="bottom" className="row-item">
            <Col span={12}>
              <div className="form-group form-group-severity">
                <label className="mandatory" htmlFor="">
                  {t('Alert severity')}
                </label>
                <EPSelect
                  value={formState.severity}
                  getPopupContainer={() => document.querySelector('.form-group.form-group-severity')}
                  disabled={isFormDisabled}
                  onChange={onChangeSeverity}
                  className="ep-select"
                >
                  <EPSelectOption value="shutdown">
                    <div className="flex align-center">
                      <SeverityIcon severity={AlertLevels.Shutdown} />
                      &nbsp;<span>{t('Shutdown')}</span>
                    </div>
                  </EPSelectOption>
                  <EPSelectOption value="warning">
                    <div className="flex align-center">
                      <SeverityIcon severity={AlertLevels.Warning} />
                      &nbsp;<span>{t('Warning')}</span>
                    </div>
                  </EPSelectOption>
                  <EPSelectOption value="service">
                    <div className="flex align-center">
                      <SeverityIcon severity={AlertLevels.Service} />
                      &nbsp;<span>{t('Service')}</span>
                    </div>
                  </EPSelectOption>
                </EPSelect>
              </div>
            </Col>
            <Col span={12}>
              <div className="form-group form-group-duration">
                <label htmlFor="">{t('Duration (if this rule lasts for x time)')}</label>
                <EPSelect
                  value={`${formState.duration}`}
                  getPopupContainer={() => document.querySelector('.form-group.form-group-duration')}
                  disabled={isFormDisabled}
                  onChange={onChangeDuration}
                  className="ep-select"
                >
                  <EPSelectOption value="0">{t('Immediately')}</EPSelectOption>
                  <EPSelectOption value="1">1 {t('minute')}</EPSelectOption>
                  <EPSelectOption value="2">2 {t('minutes')}</EPSelectOption>
                  <EPSelectOption value="5">5 {t('minutes')}</EPSelectOption>
                  <EPSelectOption value="10">10 {t('minutes')}</EPSelectOption>
                  <EPSelectOption value="30">30 {t('minutes')}</EPSelectOption>
                  <EPSelectOption value="60">1 {t('hour')}</EPSelectOption>
                </EPSelect>
              </div>
            </Col>
          </Row>
        </section>

        <section>
          <NumberedTitle number={2} title={t('Choose the parameter you wish to monitor.')} />

          <Row align="bottom" className="row-item">
            <Col span={12}>
              <div className="form-group form-group-metric">
                <label className="mandatory" htmlFor="">
                  {t('Parameter')}
                </label>
                <EPSelect
                  value={formState.metric}
                  notFoundContent={<Spin indicator={<LoadingOutlined />} size="small" />}
                  getPopupContainer={() => document.querySelector('.form-group.form-group-metric')}
                  disabled={isFormDisabled}
                  onChange={onChangeMetric}
                  className="ep-select"
                >
                  {allowedMetrics.map(metric => (
                    <EPSelectOption key={metric} value={metric}>
                      {getMetricText(metric)}
                    </EPSelectOption>
                  ))}
                </EPSelect>
              </div>
            </Col>
          </Row>
        </section>

        <section>
          <NumberedTitle
            number={3}
            title={t(
              'Choose a device with the above-selected monitored parameter. Select multiple devices to create a rule on a calculated parameter (e.g. Pressure difference between devices).',
            )}
          />
          {selectedDevicesElements}

          {formState.selectedDevices.length < 3 && formState.metric !== 'activity' && (
            <Row className="row-item">
              <Col span={24}>
                <div className="form-group">
                  <EPButton
                    icon={<PlusOutlined />}
                    disabled={!formState.metric || isFormDisabled}
                    onClick={onClickAddDevice}
                  >
                    {t('Add device')}
                  </EPButton>
                </div>
              </Col>
            </Row>
          )}
        </section>

        <section>
          <NumberedTitle
            number={4}
            title={t('Choose the operand and the target value threshold to trigger the alerts.')}
          />

          <Row align="bottom" className="row-item">
            <Col span={12}>
              <div className="form-group form-group-operand">
                <label className="mandatory" htmlFor="">
                  {t('Operand')}
                </label>
                <EPSelect
                  value={formState.operator}
                  getPopupContainer={() => document.querySelector('.form-group.form-group-operand')}
                  disabled={isFormDisabled}
                  onChange={onChangeOperator}
                  className="ep-select"
                >
                  {formState.metric === 'activity' ? (
                    <>
                      <EPSelectOption value="equalTo">=</EPSelectOption>
                      <EPSelectOption value="notEqualTo">≠</EPSelectOption>
                    </>
                  ) : (
                    <>
                      <EPSelectOption value="equalTo">=</EPSelectOption>
                      <EPSelectOption value="greaterThan">&gt;</EPSelectOption>
                      <EPSelectOption value="greaterThanOrEqualTo">&gt;=</EPSelectOption>
                      <EPSelectOption value="lessThan">&lt;</EPSelectOption>
                      <EPSelectOption value="lessThanOrEqualTo">&lt;=</EPSelectOption>
                    </>
                  )}
                </EPSelect>
              </div>
            </Col>
            <Col span={12}>
              <div className="form-group form-group-threshold">
                <label className="mandatory" htmlFor="">
                  {t('Target value')}
                </label>
                {formState.metric === 'activity' ? (
                  <EPSelect
                    value={formState.threshold}
                    getPopupContainer={() => document.querySelector('.form-group.form-group-threshold')}
                    disabled={isFormDisabled}
                    onChange={onChangeActivityThreshold}
                    className="ep-select"
                  >
                    <EPSelectOption value="load">{t('Load')}</EPSelectOption>
                    <EPSelectOption value="unload">{t('Unload')}</EPSelectOption>
                    <EPSelectOption value="off">{t('Off')}</EPSelectOption>
                  </EPSelect>
                ) : (
                  <EPInput
                    placeholder={t('E.g. 6.4')}
                    value={formState.threshold}
                    disabled={isFormDisabled}
                    onChange={onChangeThreshold}
                  />
                )}
                <div className="unit">{getMetricUnit(formState.metric)}</div>
              </div>
            </Col>
          </Row>
        </section>
      </div>

      <hr />

      <div className="form-container">
        <p className="all-done">
          {t('Save and activate to start monitoring the parameter and trigger alerts according to the rule.')}{' '}
        </p>

        <EPButton
          className="submit-btn-create"
          type="primary"
          disabled={isFormDisabled || !isFormValid() || submittingState !== 'none'}
          loading={submittingState === 'create'}
          onClick={() => onSubmit(false)}
        >
          {submitButtonTxt}
        </EPButton>

        {mode === 'create' && (
          <EPButton
            className="submit-btn-create-and-activate"
            disabled={isFormDisabled || !isFormValid()}
            loading={submittingState === 'create-and-activate'}
            onClick={() => onSubmit(true)}
          >
            {t('Create and Activate')}
          </EPButton>
        )}

        <p className="contact-us">
          {t('Contact us if you have any questions at:')} <a href="mailto:support@ecoplant.co">support@ecoplant.co</a>
        </p>
      </div>
    </Container>
  );
});

const Container = styled.div<{ theme: { token: GlobalToken } }>`
  flex-grow: 1;
  padding: ${({ theme: { token } }) => token.paddingXL}px;
  overflow: scroll;

  .form-container {
    margin-top: ${({ theme: { token } }) => token.marginXL}px;
    max-width: 670px;

    h1.title {
      font-size: ${({ theme: { token } }) => token.fontSizeHeading3}px;
      font-weight: ${({ theme: { token } }) => token.fontWeightStrong};
      color: ${({ theme: { token } }) => token.colorText};
    }

    .subtitle {
      margin-top: ${({ theme: { token } }) => token.marginSM}px;
      font-size: ${({ theme: { token } }) => token.fontSizeLG}px;
      color: ${({ theme: { token } }) => token.colorTextSecondary};
    }

    section {
      padding: ${({ theme: { token } }) => token.paddingXL}px;
      border-bottom: 1px solid ${({ theme: { token } }) => token.colorBorder};

      &:last-child {
        border-bottom-width: 0;
      }

      .row-item {
        padding-top: ${({ theme: { token } }) => token.paddingXL}px;
        padding-left: ${({ theme: { token } }) => token.paddingXL}px;
        margin-left: ${({ theme: { token } }) => token.marginXL}px;
      }

      .form-group-severity,
      .form-group-operand {
        margin-right: ${({ theme: { token } }) => token.marginSM}px;
      }

      .form-group-device-operator-description {
        margin-bottom: ${({ theme: { token } }) => token.marginSM}px;
        margin-left: ${({ theme: { token } }) => token.marginSM}px;
        font-size: ${({ theme: { token } }) => token.fontSizeSM}px;
        color: ${({ theme: { token } }) => token.colorTextSecondary};
      }

      .form-group {
        position: relative;

        label {
          display: block;
          margin-bottom: ${({ theme: { token } }) => token.marginSM}px;
          font-size: ${({ theme: { token } }) => token.fontSize}px;
          color: ${({ theme: { token } }) => token.colorTextTertiary};

          &.mandatory:before {
            content: '* ';
            color: ${({ theme: { token } }) => token.colorError};
          }
        }

        .ep-select {
          width: 100%;
        }
      }

      .form-group-threshold {
        .unit {
          position: absolute;
          bottom: 10px;
          right: -30px;
          color: ${({ theme: { token } }) => token.colorTextSecondary};
        }
      }

      .form-group-device {
        .icon-delete {
          position: absolute;
          bottom: 10px;
          left: -40px;
          cursor: pointer;
        }
      }
    }

    p.all-done {
      margin-top: ${({ theme: { token } }) => token.marginXL}px;
      font-size: ${({ theme: { token } }) => token.fontSizeLG}px;
      color: ${({ theme: { token } }) => token.colorTextSecondary};
    }

    .submit-btn-create {
      margin-top: ${({ theme: { token } }) => token.marginXL}px;
    }

    .submit-btn-create-and-activate {
      margin: ${({ theme: { token } }) => token.marginXL}px ${({ theme: { token } }) => token.marginMD}px 0;
    }

    p.contact-us {
      margin-top: ${({ theme: { token } }) => token.marginLG}px;
      font-size: ${({ theme: { token } }) => token.fontSizeSM}px;
      color: ${({ theme: { token } }) => token.colorTextTertiary};
    }
  }

  hr {
    height: 2px;
  }
`;

const NumberedTitle = ({ number, title }: { number: number; title: string }) => {
  return (
    <NumberedTitleContainer className="numbered-title">
      <div className="circled-number">{number}</div>
      <h4 className="title">{title}</h4>
    </NumberedTitleContainer>
  );
};

const NumberedTitleContainer = styled.div<{ theme: { token: GlobalToken } }>`
  display: flex;

  .circled-number {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 48px;
    height: 48px;
    border: solid 2px ${({ theme: { token } }) => token.colorBorderSecondary};
    border-radius: 50%;
    font-size: ${({ theme: { token } }) => token.fontSizeHeading3}px;
    font-weight: ${({ theme: { token } }) => token.fontWeightStrong};
    color: ${({ theme: { token } }) => token.colorText};
  }

  .title {
    display: flex;
    align-items: center;
    font-size: ${({ theme: { token } }) => token.fontSizeLG}px;
    color: ${({ theme: { token } }) => token.colorTextSecondary};
    padding-left: ${({ theme: { token } }) => token.padding}px;
  }
`;

export default CreateOrEditCustomRule;
